import { useRouterState } from "@tanstack/react-router";

/**
 * The pathless `_minimal` layout segment owns full-screen routes (e.g. the Live
 * room). When the active match tree includes it, `MainAppContent` skips the
 * member-area AppShell, top banners, cookie banner, and help widget — the route
 * owns its own chrome.
 *
 * Layout is derived from the route tree (the single source of truth) rather than
 * a hardcoded path list or a runtime `useShellBypass` toggle, so the decision is
 * made from the URL before render and the shell never mounts on these routes.
 *
 * Transitional: this hook exists only because the AppShell is still mounted
 * above the route tree in `__root.tsx`. It is removed by the "_shell lift" (see
 * the docblock on `RootLayout` in `tanstack_routes/__root.tsx`).
 */
const MINIMAL_SEGMENT_IDS = new Set(["/_minimal", "/_protected/_embed"]);

export const useIsMinimalLayoutRoute = (): boolean =>
  useRouterState({
    // TanStack Router includes every ancestor in `state.matches`, so the
    // pathless `_minimal` layout route is present whenever any route under it is
    // active — an exact id match is enough (and avoids matching a sibling like
    // `/_minimalfoo`).
    select: state =>
      state.matches.some((match: { routeId: string }) =>
        MINIMAL_SEGMENT_IDS.has(match.routeId),
      ),
  });
